import { useMemo } from 'react';
import { SmartReportTypeEnum } from '@/admins/SmartReports/types';
import { useGetAssets } from '@/hooks/useGetAssets';
import { useGetCompanies } from '@/hooks/useGetCompanies';
import { useGetCountries } from '@/hooks/useGetCountries';
import { useGetReportTypes } from './useGetReportTypes';

export const useGetEntities = ({ reportTypeId, search = '' }) => {
  const { reportTypes, loading: loadingReportTypes } = useGetReportTypes();

  const reportType = useMemo(() => {
    if (!reportTypeId) return null;

    const type = reportTypes.find(type => type.id === reportTypeId);
    return type || null;
  }, [reportTypeId, reportTypes]);

  const { assets, loading: loadingAssets } = useGetAssets({
    search,
    skip: reportType?.name !== SmartReportTypeEnum.Asset,
  });

  const { countries, loading: loadingCountries } = useGetCountries({
    skip: reportType?.name !== SmartReportTypeEnum.Country,
  });

  const { companies, loading: loadingCompanies } = useGetCompanies({
    variables: { search },
    skip: reportType?.name !== SmartReportTypeEnum.Corporate,
  });

  const entities = useMemo(() => {
    if (!reportType) return [];

    const allEntities = {
      [SmartReportTypeEnum.Asset]: assets,
      [SmartReportTypeEnum.Country]: countries,
      [SmartReportTypeEnum.Corporate]: companies,
    };

    const entities = allEntities[reportType.name] || [];
    return entities.map(entity => ({
      ...entity,
      label: entity.name,
      value: entity.id,
      id: entity.id,
    }));
  }, [reportType, assets, countries, companies]);

  return {
    entities,
    loading: loadingReportTypes || loadingAssets || loadingCountries || loadingCompanies,
    reportType,
  };
};
